import React, { useState} from "react"
import { ScrollView } from "~/ui/primitives"
import { observer } from "mobx-react-lite"
import { useFormik } from "formik"
// import { useSafeAreaInsets } from "~/ui/safe-area"
import Clipboard from "~/ui/clipboard"
// import { AvatarAccordion } from "~components/accordion"
import styled, { useTheme } from "~/ui/styled"
import { ColumnFlex, IconFont, CenterFlex, Text, Button } from "~components/El"
import { ChatTypes } from "~proto"
import { AddChannelSchema } from "~schema"
import { useStores } from "~models"
// import RadioAccordion from "~components/accordion/radio-accordion"
import { TextField, MultiLineTextField } from "~components/InputField"
import { FeildFloatLabel } from "~widgets/feild-float-label"
import AvatarUpload from "./components/AvatarUpload"
import useNavigation from "~hooks/useNavigation"
import useGroupActions from "~hooks/useGroupActions"
import { useModal } from "~hooks/useModal"
import { globalToast } from "~utils"
import { WEB_URL } from "~config/env"
import { scrollViewFillStyle } from "~/ui/scroll-layout"

const Container = styled(ColumnFlex)`
  flex: 1;
  min-height: 0;
  width: 100%;
  background-color: ${({ theme }) => theme.colors.blockBackground};
  overflow: hidden;
`
const ButtonFixed = styled(Button)`
  width: 100%;
  height: 44px;
  margin-top: 40px;
  border-radius: 8px;
  background-color: ${({ theme }) => theme.colors.primary};
`

const CopyRow = styled(Button)`
  width: 100%;
  padding: 12px;
  border-radius: 8px;
  background-color: ${({ theme }) => theme.colors.background};
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  text-align: left;
`

const onCopy = (v: string) => {
  if (!v) return
  Clipboard.setString(v)
  globalToast.show("复制成功")
}

function getChannelInviteLink(cid: string) {
  if (!cid) return ""
  const base = WEB_URL.replace(/\/$/, "")
  return `${base}/channel/${cid}`
}

const RenderResultModal = ({ cid = "" }: { cid?: string }) => {
  const theme = useTheme()
  const inviteLink = getChannelInviteLink(cid)

  return (
    <ColumnFlex width="100%" height="auto">
      <CenterFlex width="100%" minHeight="max-content">
        <IconFont name="cg" size={48} color={theme.colors.primary} />
      </CenterFlex>
      <Text
        fontSize="16px"
        fontWeight={600}
        color={theme.colors.text}
        style={{ marginTop: 12, marginBottom: 16, textAlign: "center", width: "100%" }}
      >
        频道创建成功!
      </Text>
      <CopyRow onClick={() => onCopy(cid)}>
        <Text fontSize="15px" color={theme.colors.secondary}>
          频道ID：<Text fontSize="13px" color={theme.colors.text}>{cid}</Text>
        </Text>
        <IconFont name="copy" size={14} color={theme.colors.primaryText} />
      </CopyRow>
      <CopyRow style={{ marginTop: 10, alignItems: "flex-start" }} onClick={() => onCopy(inviteLink)}>
        <ColumnFlex flex={1} minWidth={0} mr="8px" height="auto">
          <Text fontSize="15px" color={theme.colors.secondary}>
            邀请链接：
          </Text>
          <Text
            fontSize="13px"
            color={theme.colors.primaryText}
            style={{ marginTop: 4, wordBreak: "break-all", lineHeight: "18px" }}
          >
            {inviteLink}
          </Text>
        </ColumnFlex>
        <IconFont name="copy" size={14} color={theme.colors.primaryText} />
      </CopyRow>
    </ColumnFlex>
  )
}

const AddChannel: React.FC = () => {
  const theme = useTheme()
  const { chatStore, userStore } = useStores()
  // const insets = useSafeAreaInsets()
  const navigation = useNavigation()
  const { handleCreateChatRoom } = useGroupActions()
  const { Dialog } = useModal()
  const [loading, setLoading] = useState(false)

  const formik = useFormik({
      initialValues: {
          image: "", // 频道头像
          title: "", // 频道名称
          desc: "", // 频道描述
          is_private: 0 // 频道类型
      },
      validationSchema: AddChannelSchema,
      onSubmit: (values) => {
        if (loading) return
        setLoading(true)
        const chat = chatStore.newGroupChat({
          type: ChatTypes.CHANNEL,
          creator_id: userStore.currentUser.id,
          image: values.image,
          title: values.title || "频道",
          members: [{ user_id: userStore.currentUser.id, user: userStore.currentUser }],
        })
        handleCreateChatRoom({
          ...values,
          type: chat.type,
          chat_id: chat.chat_id,
          title: chat.title,
          image: chat.image,
          members: [userStore.currentUser.id],
        }).then(data => {
          setLoading(false)
          Dialog.show({
            title: "",
            closeOnAction: false,
            content: React.createElement(RenderResultModal, { cid: data?.res?.name || "" }),
            onConfirm: () => {
              chatStore.setCurrent(chat)
              navigation.goBack()
            },
          })
        }).catch(error => {
          setLoading(false)
          globalToast.show(error?.message || "创建频道失败")
        })
        
      },
  });
  const { values, errors, touched, setFieldValue, handleSubmit } = formik;

  return (
      <Container>
        <ScrollView
          style={scrollViewFillStyle}
          keyboardShouldPersistTaps="handled"
          showsVerticalScrollIndicator={false}
          contentContainerStyle={{ paddingBottom: 100, paddingHorizontal: 15 }} // 给底部按钮留空间
        >
          <AvatarUpload
            image={values.image}
            onChange={({ uri }) => {
              setFieldValue("image", uri);
            }}
          />
          <TextField
            label="频道名称"
            suffix={<Text fontSize={12} color={theme.colors.secondaryText}>{values.title.length}/20</Text>}
            value={values.title}
            placeholder="请输入频道名称"
            required={true}
            onChangeText={(value) => setFieldValue("title", value)}
            error={touched.title && errors.title}
          />
          <MultiLineTextField
            label="频道描述"
            value={values.desc}
            placeholder="介绍一下您的频道..."
            maxLength={500}
            style={{ height: 200 }}
            onChangeText={(v) => setFieldValue("desc", v)}
            error={touched.desc && errors.desc}
          />
          {/* <RadioAccordion
            label="频道类型"
            items={[
              { label: "公开频道", value: 0, desc: "任何人都可以搜索并订阅", icon: "tzk" },
              { label: "私有频道", value: 1, desc: "部分人可以搜索并订阅", icon: "tzg" },
            ]}
            renderItemLabel={RenderItemLabel}
            value={values.is_private}
            onChange={(v) => setFieldValue('is_private', v)}
          /> */}
          <FeildFloatLabel>频道规则</FeildFloatLabel>
          <ColumnFlex height="auto"  gap={4} style={{ marginTop: 4 }}>
            <Text fontSize={12} color={theme.colors.secondary}>
              1. 频道为广播模式，只有频道主和管理员可以发布内容。
            </Text>
            <Text fontSize={12} color={theme.colors.secondary}> 2. 订阅者只能接收和浏览频道内容。</Text>
            <Text fontSize={12} color={theme.colors.secondary}> 3. 频道主可以设置担保资金来提升频道信誉。</Text>
            <Text fontSize={12} color={theme.colors.secondary}> 4. 所有资金操作都会在频道内公开广播。</Text>
          </ColumnFlex>
          <ButtonFixed disabled={loading} loading={loading} onClick={handleSubmit} >
            <Text fontSize="16px" color={theme.colors.textLight}>
              创建频道
            </Text>
          </ButtonFixed>
        </ScrollView>
      </Container>
  )
}

export default observer(AddChannel)
